import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'

import { useIntl } from 'react-intl'

import { useCMSPageField } from '@/cms/fields'
import { Custom500 } from '@/pages/500'
import { CheckoutError } from '@/scopes/checkout/components/CheckoutError/CheckoutError'
import { CheckoutFooter } from '@/scopes/checkout/components/CheckoutFooter'
import { CheckoutHeader } from '@/scopes/checkout/components/CheckoutHeader'
import { CheckoutHeading, CheckoutHeadingTitle } from '@/scopes/checkout/components/CheckoutHeading'
import { CheckoutNextStep } from '@/scopes/checkout/components/CheckoutNextStep/CheckoutNextStep'
import { CheckoutProgress } from '@/scopes/checkout/components/CheckoutProgress'
import { CheckoutUsps } from '@/scopes/checkout/components/CheckoutUsps/CheckoutUsps'
import {
  Content,
  Loader,
  MainContent,
  PageLayout,
  PrimaryContent,
  SideBarContent,
} from '@/scopes/checkout/components/Layout'
import { useSendCheckoutEvent } from '@/scopes/checkout/domain/analytics/useSendCheckoutEvent'
import { CheckoutReactContext } from '@/scopes/checkout/domain/CheckoutContext'
import { CheckoutOptimizelyFlagsProvider } from '@/scopes/checkout/domain/CheckoutOptimizelyFlagsProvider'
import { useBookingDraftQuery } from '@/scopes/checkout/domain/hooks'
import { DevTools } from '@/scopes/common/components/DevTools/DevTools'
import { MetaTitle } from '@/scopes/common/components/MetaTitle'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { useFeatureFlag } from '@/scopes/common/infrastructure/feature-flags'
import { NewRelicErrorBoundary } from '@/scopes/common/infrastructure/logging/NewRelicErrorBoundary'
import { Basket } from '../components/Basket'
import { CheckoutBenefits } from '../components/CheckoutBenefits/CheckoutBenefits'
import { EngineError } from '../components/EngineError/EngineError'
import { CheckoutItems } from './CheckoutItems'

const queryClient = new QueryClient()

function Page() {
  const { isLoading, data } = useBookingDraftQuery()
  const intl = useIntl()
  const brandName = useConfiguration('brandName')

  useSendCheckoutEvent('checkout_2_reached')

  const isNewBasketEnabled = useFeatureFlag('MALLORCA_ENABLE_NEW_BASKET')

  return (
    <PageLayout>
      {data?.error && data.error.hasError
        ? (
            !data.error.errorCode && data.error.redirectUrl
              ? (
                  <CheckoutError />
                )
              : (
                  <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand={brandName} />}>
                    <CheckoutHeader />
                    <main id="main-content" tabIndex={-1}>
                      <EngineError error={data.error} />
                    </main>
                  </NewRelicErrorBoundary>
                )
          )
        : (
            <NewRelicErrorBoundary scope="checkout-page" fallback={<Custom500 brand={brandName} />}>
              <CheckoutHeader />
              <MainContent>
                {isLoading
                  ? (
                      <Loader />
                    )
                  : (
                      <>
                        <CheckoutProgress />
                        <CheckoutHeading>
                          <CheckoutHeadingTitle>
                            {intl.formatMessage({ id: 'checkout_steps_header_step_2_title' })}
                          </CheckoutHeadingTitle>
                        </CheckoutHeading>
                        <Content>
                          <PrimaryContent>
                            <CheckoutItems />
                            <CheckoutNextStep />
                            <CheckoutUsps />
                          </PrimaryContent>
                          <SideBarContent>
                            <Basket hidePriceSummary />
                            {!isNewBasketEnabled && <CheckoutBenefits />}
                          </SideBarContent>
                        </Content>
                      </>
                    )}
              </MainContent>
              {!isLoading && <CheckoutFooter />}
            </NewRelicErrorBoundary>
          )}
    </PageLayout>
  )
}

export default function CheckoutPageStep2() {
  const contextItemId = useCMSPageField('contextItemId')
  const checkoutContextValue = React.useMemo(() => ({
    contextItemId,
    step: 2,
  }), [contextItemId])

  return (
    <QueryClientProvider client={queryClient}>
      <CheckoutReactContext value={checkoutContextValue}>
        <CheckoutOptimizelyFlagsProvider>
          <MetaTitle titleTranslationTag="checkout_steps_step_2_meta_title" />
          <Page />
          <DevTools />
        </CheckoutOptimizelyFlagsProvider>
      </CheckoutReactContext>
    </QueryClientProvider>
  )
}
